import React from 'react';

import { useTranslation } from 'react-i18next';

import { Input, Pagination } from 'design-system-web';

import { Loading } from 'components/general';

import type { ChannelListItem } from '../hooks/useBulkChannelSelection';
import { useParentChannelSearch } from '../hooks/useParentChannelSearch';

import styles from '../styles.scss';

type Props = {
  selectedChannelId: string | null;
  onChannelSelected: (channel: ChannelListItem | null) => void;
};

export function ParentChannelPicker({
  selectedChannelId,
  onChannelSelected,
}: Props) {
  const { t } = useTranslation();
  const {
    channels,
    loading,
    keyword,
    setKeyword,
    total,
    page,
    setPage,
    pageSize,
  } = useParentChannelSearch();

  const handleKeywordChange = (value: string, _fieldName: string) => {
    setKeyword(value);
  };

  return (
    <div className={styles.parentChannelPicker}>
      <Input
        fieldName="bulkParentChannelSearch"
        placeholder={t(
          'web.admin.portalManagement.channelModulesBulk.mode.parent.searchPlaceholder'
        )}
        value={keyword}
        onChange={handleKeywordChange}
        maxExpand
        testId="bulkParentChannelSearchInput"
      />

      <div className={styles.parentChannelList}>
        {loading && (
          <div
            className={styles.parentChannelListSpinner}
            data-test="parentChannelPickerLoading"
          >
            <Loading />
          </div>
        )}

        {!loading && channels.length === 0 && (
          <div className={styles.parentChannelEmpty}>
            {t(
              'web.admin.portalManagement.channelModulesBulk.mode.parent.noParentsFound'
            )}
          </div>
        )}

        {!loading &&
          channels.map(ch => {
            const selected = ch._id === selectedChannelId;

            return (
              <button
                key={ch._id}
                type="button"
                className={`${styles.parentChannelRow} ${
                  selected ? styles.parentChannelRowSelected : ''
                }`}
                onClick={() => onChannelSelected(ch)}
                data-test={`bulkParentChannelOption-${ch._id}`}
              >
                <span
                  className={`${styles.parentChannelRowName} ${
                    selected ? styles.parentChannelRowNameSelected : ''
                  }`}
                >
                  {ch.name}
                </span>
                <span className={styles.parentChannelRowType}>{ch.type}</span>
              </button>
            );
          })}
      </div>

      {total > pageSize && (
        <div className={styles.parentPickerPagination}>
          <Pagination
            loading={loading}
            total={total}
            page={page}
            perPage={pageSize}
            onPage={setPage}
            testId="bulkParentChannelPagination"
          />
        </div>
      )}
    </div>
  );
}
